// mapPmAdapter.js — Extract and transform map PM document state.

import {yXmlFragmentToProseMirrorRootNode} from 'y-prosemirror';
import {DEFAULT_VIEW_ID, mapSchema, newLayerId, MAP_FIT_PADDING} from './mapSchema';
import {roundCoord} from './geoCoords';

export {MAP_FIT_PADDING};

// ticket 02 (layer-panel-rework): fixed default color for free-drawn/tactical-graphic (non-
// mil-symbol) markings now that per-layer color customization is removed — mil-symbol markings
// get their color from SIDC/affiliation via mil-sym-ts-web and never read this.
export const DEFAULT_MARKING_COLOR = '#3b82f6';

export function flattenCoords(coords) {
    if (!Array.isArray(coords)) return [];
    if (typeof coords[0] === 'number') return [coords];
    return coords.flatMap(flattenCoords);
}

/**
 * MINSKY-16404 payload-shape overhaul: flatten a GeoJSON geometry's coordinates down to a single
 * representative `{lat, lon}` pair, rounded to 5 decimals — used to replace the raw GeoJSON
 * `{type, coordinates}` wrapper the sitrep payload previously emitted (see buildSitrepPayload.js/
 * dtgDiff.js). Point geometries resolve exactly; multi-point geometries (phase lines, boundaries,
 * free-drawn polygons) use their first coordinate as a deliberately simple representative point —
 * this payload doesn't attempt to preserve full line/polygon shape (that would reintroduce the
 * deep-nesting problem the review flags), a judgment call documented here and in
 * buildSitrepPayload.js's/dtgDiff.js's own doc comments.
 *
 * @param {{coordinates?: unknown}|null|undefined} geometry
 * @returns {{lat: number, lon: number}|null}
 */
export function geometryToLatLon(geometry) {
    if (!geometry?.coordinates) return null;
    try {
        const pts = flattenCoords(geometry.coordinates);
        if (pts.length === 0) return null;
        const [lon, lat] = pts[0];
        if (typeof lon !== 'number' || typeof lat !== 'number') return null;
        if (!Number.isFinite(lon) || !Number.isFinite(lat)) return null;
        return {lat: roundCoord(lat), lon: roundCoord(lon)};
    } catch (_) {
        return null;
    }
}

export function featureBbox(geometry) {
    if (!geometry?.coordinates) return null;
    try {
        const pts = flattenCoords(geometry.coordinates);
        if (pts.length === 0) return null;
        let minLng = Infinity, minLat = Infinity, maxLng = -Infinity, maxLat = -Infinity;
        for (const [lng, lat] of pts) {
            minLng = Math.min(minLng, lng);
            maxLng = Math.max(maxLng, lng);
            minLat = Math.min(minLat, lat);
            maxLat = Math.max(maxLat, lat);
        }
        return [[minLng, minLat], [maxLng, maxLat]];
    } catch (_) {
        return null;
    }
}

export function unionBbox(bboxes) {
    const valid = bboxes.filter(Boolean);
    if (valid.length === 0) return null;
    let minLng = Infinity, minLat = Infinity, maxLng = -Infinity, maxLat = -Infinity;
    for (const [[bMinLng, bMinLat], [bMaxLng, bMaxLat]] of valid) {
        minLng = Math.min(minLng, bMinLng);
        maxLng = Math.max(maxLng, bMaxLng);
        minLat = Math.min(minLat, bMinLat);
        maxLat = Math.max(maxLat, bMaxLat);
    }
    return [[minLng, minLat], [maxLng, maxLat]];
}

/** Ensure a bbox has non-zero span so fitBounds works for points and thin lines. */
export function bboxForFit(bbox, minSpanDegrees = 0.002) {
    if (!bbox) return null;
    const [[minLng, minLat], [maxLng, maxLat]] = bbox;
    const lngSpan = Math.max(maxLng - minLng, minSpanDegrees);
    const latSpan = Math.max(maxLat - minLat, minSpanDegrees);
    const lngCenter = (minLng + maxLng) / 2;
    const latCenter = (minLat + maxLat) / 2;
    return [
        [lngCenter - lngSpan / 2, latCenter - latSpan / 2],
        [lngCenter + lngSpan / 2, latCenter + latSpan / 2],
    ];
}

// ticket 01 (map-editor-audit): `extractMapState` re-walks the whole PM doc from scratch on
// every call, but is invoked 3-4x per transaction against the SAME `doc` (MapEditorModal.jsx's
// `rawLayersForFilter`/`rawMarkingsForFilter`, useMapEditor.js's `layerShapeCounts` + sync
// effect). A PM `doc` is immutable — every transaction produces a brand-new `doc` object — so a
// `WeakMap` keyed by `doc` is a transparent, self-invalidating cache: repeated calls against the
// same `doc` reference are free, and a real edit (new `doc`) always re-walks. `WeakMap` also
// means old docs are collected once nothing else references them, with no manual cache eviction.
const extractMapStateCache = new WeakMap();

/** Walk a map PM doc and return structured state. */
export function extractMapState(doc) {
    const cached = extractMapStateCache.get(doc);
    if (cached) return cached;

    // shared-map-data ticket 03: `map_meta` only holds the shared DTG range now (title/basemap/
    // grid/overlay/satellite-cursor moved to per-node mapNode attrs — see mapNode.js) — no
    // meaningful defaults to seed here beyond what `dtgStartMs`/`dtgEndMs`'s own schema defaults
    // already provide via `Object.assign` below.
    const meta = {};
    const layers = [];
    const markings = [];
    const views = [];

    doc.forEach((node) => {
        if (node.type.name === 'map_meta') {
            Object.assign(meta, node.attrs);
        } else if (node.type.name === 'map_layer') {
            layers.push({...node.attrs});
        } else if (node.type.name === 'map_marking') {
            markings.push({...node.attrs});
        } else if (node.type.name === 'map_view') {
            views.push({...node.attrs});
        }
    });

    layers.sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
    views.sort((a, b) => (a.name || '').localeCompare(b.name || ''));

    const result = {meta, layers, markings, views};
    extractMapStateCache.set(doc, result);
    return result;
}

/**
 * Bug fix (report: "'All markings' view crops to all markings of all DTGs" instead of just the
 * ones for the selected DTG): this used to resolve every marking via
 * `resolveTrackAtDtg(m, null)` UNCONDITIONALLY — `null` means "no live cursor" ->
 * `resolveTrackAtDtg` falls back to each track's chronologically LATEST-EVER snapshot,
 * regardless of what DTG is actually selected. So a marking that has since moved further after
 * the selected DTG (or moved at all, if the user has scrubbed the timeline back in time) had its
 * *future* position — not its position at the selected DTG — pulled into the "All markings"
 * union, inflating the crop with a position that isn't even being rendered right now.
 *
 * Now takes `cursorDtg` (epoch ms) and forwards it to `resolveTrackAtDtg` so each marking
 * resolves to its actual current-at-that-DTG snapshot — same "in view at DTG" visibility rule as
 * `layersVisibleAtDtg`/`layersVisibleInDtgRange` below (unbounded staleness — a "last known"
 * fallback pick is still included, only a track that hasn't started yet at `cursorDtg` is
 * excluded via `resolved.visible`). `cursorDtg == null` (callers that don't have a live DTG
 * cursor wired in) preserves the original "show latest known state" fallback, so it stays a
 * strict superset fix — no caller's behavior changes unless it now passes a real cursor.
 *
 * @param {Array} markings
 * @param {Array} layers
 * @param {number|null|undefined} [cursorDtg] - epoch ms DTG cursor; forwarded as-is to
 *   `resolveTrackAtDtg` (see its own doc comment for exact semantics).
 */
export function computeDefaultViewBbox(markings, layers, cursorDtg) {
    const layerMap = new Map(layers.map((l) => [l.layerId, l]));
    const bboxes = markings
        .filter((m) => layerMap.get(m.layerId)?.visible !== false)
        .map((m) => {
            const resolved = resolveTrackAtDtg(m, cursorDtg);
            return resolved.visible ? resolved.snapshot?.geometry : null;
        })
        .filter(Boolean)
        .map(featureBbox);
    return unionBbox(bboxes);
}

/**
 * @param {string|null|undefined} viewId
 * @param {Array} views
 * @param {Array} markings
 * @param {Array} layers
 * @param {number|null|undefined} [cursorDtg] - epoch ms DTG cursor, forwarded to
 *   `computeDefaultViewBbox` (see its doc comment) for the "All markings" / no-stored-bbox
 *   fallback path only — a NAMED view with its own stored `bbox` (the common case) returns that
 *   bbox verbatim and never resolves any marking, so it is unaffected by `cursorDtg` entirely.
 */
export function resolveViewBbox(viewId, views, markings, layers, cursorDtg) {
    if (!viewId || viewId === DEFAULT_VIEW_ID) {
        return bboxForFit(computeDefaultViewBbox(markings, layers, cursorDtg));
    }
    const view = views.find((v) => v.viewId === viewId);
    if (view?.bbox) return view.bbox;
    return bboxForFit(computeDefaultViewBbox(markings, layers, cursorDtg));
}

/** Named crop views fit exactly; "All markings" gets pixel padding around the union bbox. */
export function resolveViewFitPadding(viewId) {
    if (!viewId || viewId === DEFAULT_VIEW_ID) return MAP_FIT_PADDING;
    return 0;
}

/** Per-embed view selection from the report map node attrs only. */
export function resolveActiveViewId(nodeAttrs) {
    if (nodeAttrs?.viewId === DEFAULT_VIEW_ID) return null;
    return nodeAttrs?.viewId ?? null;
}

/** View id for the embed chrome dropdown. */
export function resolveDisplayViewId(nodeAttrs) {
    if (nodeAttrs?.viewId === DEFAULT_VIEW_ID) return DEFAULT_VIEW_ID;
    if (nodeAttrs?.viewId) return nodeAttrs.viewId;
    return DEFAULT_VIEW_ID;
}

export function viewFitKey(viewId, bbox) {
    const id = !viewId || viewId === DEFAULT_VIEW_ID ? DEFAULT_VIEW_ID : viewId;
    if (!bbox) return id;
    return `${id}:${bbox[0][0]},${bbox[0][1]},${bbox[1][0]},${bbox[1][1]}`;
}

/** Ensure new/opened maps have at least one layer so drawing tools work immediately. */
export function ensureDefaultMapLayer(state) {
    const {layers} = extractMapState(state.doc);
    if (layers.length > 0) return state;

    const layerId = newLayerId();
    const node = mapSchema.nodes.map_layer.create({
        layerId,
        label: 'Layer 1',
        visible: true,
        order: 0,
    });
    return state.apply(state.tr.insert(state.doc.content.size, node));
}

/**
 * Layers that have at least one visible marking intersecting the view bbox.
 *
 * NB (ticket 06): despite being flagged as dead code when ticket 06 was scoped, this function
 * is in fact live — `MapNodeView.js#_refreshChrome` calls it to build the embedded-map legend
 * (`legendLayers`). It intentionally has NO knowledge of the DTG cursor (the embed chrome has
 * no live cursor to read). Left untouched here on purpose: ticket 06's live-editor Layers-panel
 * filtering (which DOES need DTG resolution) is implemented as the separate `layersVisibleAtDtg`
 * below rather than by changing this function's behavior/signature, so the legend keeps rendering
 * exactly as before.
 */
export function layersInView(layers, markings, viewBbox) {
    if (!viewBbox) return layers.filter((l) => l.visible !== false);
    const [sw, ne] = viewBbox;
    const inBbox = (geometry) => {
        const bb = featureBbox(geometry);
        if (!bb) return false;
        return !(bb[1][0] < sw[0] || bb[0][0] > ne[0] || bb[1][1] < sw[1] || bb[0][1] > ne[1]);
    };
    // ticket 01: `geometry` only lives inside `snapshots` now — this legend has no live DTG
    // cursor to resolve against, so (like computeDefaultViewBbox above) fall back to each
    // track's latest known snapshot geometry.
    const layerIds = new Set(
        markings
            .filter((m) => {
                const geometry = resolveTrackAtDtg(m, null).snapshot?.geometry;
                return geometry && inBbox(geometry);
            })
            .map((m) => m.layerId)
    );
    return layers.filter((l) => l.visible !== false && layerIds.has(l.layerId));
}

/**
 * ticket 06: layers panel viewport + DTG filtering.
 *
 * Like `layersInView` above, but resolves each marking through ticket 04's `resolveTrackAtDtg`
 * before doing the bbox intersection test, so a layer only qualifies if it has ≥1 marking that
 * is BOTH (a) resolved as `visible: true` at `cursorDtg` and (b) whose resolved geometry (the
 * picked snapshot's geometry — ticket 01 made `snapshots` the only place geometry lives, so a
 * marking with no snapshot at all has nothing to resolve and is skipped) intersects `viewBbox`.
 *
 * Pure/no OL or React dependency — safe to unit test directly. This is intentionally a
 * *separate* function from `layersInView` (see that function's doc comment for why): the two
 * have different callers with different needs (embed-chrome legend vs. live-editor panel) and
 * `layersInView` has a real existing caller whose behavior must not change.
 *
 * Design note on `activeLayerId` (ticket 06 requirement: never let the active/drawing-target
 * layer disappear from the panel by panning/scrubbing away from it): that concern is deliberately
 * OUT of scope for this pure function. It is handled one layer up, at the MapEditorModalInner /
 * LayerPanel integration point, which force-includes the active layer id in the final panel list
 * even when this function excludes it. Keeping that policy out of the pure filter keeps the
 * filter's contract simple ("what's actually in view/time") and keeps the "never lose your draw
 * target" UI policy colocated with the rest of the panel-wiring code that already knows about
 * `activeLayerId`.
 *
 * @param {Array} layers - `map_layer` node attrs (as from `extractMapState`).
 * @param {Array} markings - `map_marking` node attrs (as from `extractMapState`); each marking is
 *   treated as its own track for `resolveTrackAtDtg` purposes (reads its `snapshots`/`mobility`).
 * @param {[[number,number],[number,number]]|null} viewBbox - `[[swLng,swLat],[neLng,neLat]]`, or
 *   `null` if the viewport isn't known yet (e.g. map not ready) — bbox gating is skipped in that
 *   case (every marking is treated as "in view"), but DTG gating still applies.
 * @param {number|null|undefined} cursorDtg - epoch ms DTG cursor; forwarded as-is to
 *   `resolveTrackAtDtg` (so `null`/`undefined` means "show latest known state", same semantics).
 * @returns {Array} the subset of `layers` (same shape as input) that qualify. Layers explicitly
 *   hidden (`visible === false`) never qualify, matching `layersInView`/`markingsToGeoJSON`.
 */
export function layersVisibleAtDtg(layers, markings, viewBbox, cursorDtg) {
    const inBbox = (geometry) => {
        if (!viewBbox) return true;
        const bb = featureBbox(geometry);
        if (!bb) return false;
        const [sw, ne] = viewBbox;
        return !(bb[1][0] < sw[0] || bb[0][0] > ne[0] || bb[1][1] < sw[1] || bb[0][1] > ne[1]);
    };

    const layerIds = new Set();
    for (const m of markings) {
        if (!m.layerId || layerIds.has(m.layerId)) continue;
        const resolved = resolveTrackAtDtg(m, cursorDtg);
        if (!resolved.visible || !resolved.snapshot) continue;
        const geometry = resolved.snapshot.geometry;
        if (geometry && inBbox(geometry)) layerIds.add(m.layerId);
    }
    return layers.filter((l) => l.visible !== false && layerIds.has(l.layerId));
}

/**
 * ticket 03 (map-timeline-range-rework): layers panel viewport + DTG-*range*-overlap filtering.
 *
 * Sibling to `layersVisibleAtDtg` above, for the live-editor Layers panel now that the timeline
 * has an editable `[start, end]` range (ticket 02's `useDtgRange`) instead of a single scrub
 * point: a layer qualifies only if BOTH —
 *   (a) its combined marking-snapshot DTG range (`computeLayerDtgRange`, over ALL of its
 *       markings' snapshots, independent of the current scrub position) overlaps the selected
 *       `[rangeStart, rangeEnd]` — a layer with no snapshot history at all (`{start: null, end:
 *       null}`) never overlaps anything, matching `layersVisibleAtDtg`'s prior behavior of
 *       excluding markings with no snapshots; and
 *   (b) it has >=1 marking whose position resolves (via `resolveTrackAtDtg` at the current
 *       scrub point `cursorDtg` — same bbox-intersection mechanic as `layersVisibleAtDtg`,
 *       consistent with what's actually rendered on the map right now) inside `viewBbox`.
 *
 * `layersVisibleAtDtg` above is deliberately left as its own separate, unmodified function
 * (with its own passing test suite) rather than rewritten in place — this filter has a
 * different contract (range overlap, not single-point visibility) and a different caller
 * (MapEditorModal.jsx's `inViewLayerIdSet` memo now calls this one instead).
 *
 * @param {Array} layers - `map_layer` node attrs (as from `extractMapState`).
 * @param {Array} markings - `map_marking` node attrs (as from `extractMapState`).
 * @param {[[number,number],[number,number]]|null} viewBbox - same contract as
 *   `layersVisibleAtDtg`'s `viewBbox` — `null` skips bbox gating (every marking is "in view").
 * @param {number|null|undefined} cursorDtg - epoch ms scrub position, used only to resolve each
 *   marking's on-map position for the bbox check (forwarded to `resolveTrackAtDtg` as-is).
 * @param {number} rangeStart - epoch ms, inclusive lower bound of the selected DTG range.
 * @param {number} rangeEnd - epoch ms, inclusive upper bound of the selected DTG range.
 * @returns {Array} the subset of `layers` that qualify. Layers explicitly hidden
 *   (`visible === false`) never qualify, matching `layersVisibleAtDtg`/`layersInView`.
 */
export function layersVisibleInDtgRange(layers, markings, viewBbox, cursorDtg, rangeStart, rangeEnd) {
    const inBbox = (geometry) => {
        if (!viewBbox) return true;
        const bb = featureBbox(geometry);
        if (!bb) return false;
        const [sw, ne] = viewBbox;
        return !(bb[1][0] < sw[0] || bb[0][0] > ne[0] || bb[1][1] < sw[1] || bb[0][1] > ne[1]);
    };

    const bboxQualifiedLayerIds = new Set();
    for (const m of markings) {
        if (!m.layerId || bboxQualifiedLayerIds.has(m.layerId)) continue;
        const resolved = resolveTrackAtDtg(m, cursorDtg);
        if (!resolved.visible || !resolved.snapshot) continue;
        const geometry = resolved.snapshot.geometry;
        if (geometry && inBbox(geometry)) bboxQualifiedLayerIds.add(m.layerId);
    }

    return layers.filter((l) => {
        if (l.visible === false) return false;
        if (!bboxQualifiedLayerIds.has(l.layerId)) return false;
        const { start, end } = computeLayerDtgRange(l.layerId, markings);
        if (start === null || end === null) return false;
        return start <= rangeEnd && end >= rangeStart;
    });
}

/**
 * ticket 04: resolve which snapshot of a marking's append-only `snapshots` history should be
 * drawn for a given DTG cursor value, and whether that snapshot is "current" (i.e. confirmed /
 * up to date) or stale ("last known").
 *
 * Pure and testable in isolation — no OL/DOM dependencies — so the render pipeline (useMapEditor
 * feature-sync, SymbolRenderer/TacticalRenderer style functions) can stay thin wrappers around it.
 *
 * @param {{snapshots?: Array<{dtg:number, geometry:object|null, props?:object}>|null, mobility?: 'stationary'|'moving'|null}} track
 *   A marking's attrs (or any object shaped like one) — only `snapshots` and `mobility` are read.
 * @param {number|null|undefined} cursorDtg - epoch ms DTG cursor value. `null`/`undefined` means
 *   "no cursor" (e.g. caller hasn't wired ticket 01's useDtgRange) — treated as "show latest".
 * @param {number|null|undefined} [maxAgeDays] - ticket 04 (merge-last-known-into-trail): optional
 *   staleness ceiling, in days, that a non-exact-match ("last known") pick must fall within to
 *   still be rendered. `null`/`undefined` (the default, used by every caller that predates this
 *   ticket) means "no ceiling" — preserves the original unbounded "always show nearest earlier
 *   snapshot" behavior. Callers that DO care (the live editor's current-icon render path) pass
 *   `0` when the "Show trail" toggle is off (any non-exact pick is too stale to show) or
 *   `trailWindowDays` when it's on (a pick is shown with the "last known" badge only while its
 *   age is within the trail window; older picks are suppressed exactly like `visible: false`
 *   below, not just styled differently). Never applied when the pick IS current (exact match or
 *   a stationary track) — a current pick always renders regardless of this bound.
 * @returns {{snapshot: {dtg:number, geometry:object|null, props?:object}|null, isCurrent: boolean, visible: boolean}}
 *   - `visible: false` → don't render this track at all — either it starts after `cursorDtg`, or
 *     (ticket 04/merge-last-known-into-trail) its resolved pick is stale beyond `maxAgeDays`.
 *   - `snapshot: null` → no snapshot history at all, OR (see above) suppressed for staleness.
 *     Ticket 01 made `snapshots` the sole source of truth for a marking's
 *     geometry/sidc/sidcModifiers/label/affiliation (there's no top-level attr fallback anymore),
 *     so a `null` snapshot with `visible: true` should only happen for a malformed/legacy doc;
 *     callers should treat any `snapshot: null` as "nothing to render".
 *   - `isCurrent: false` → the resolved snapshot's `dtg` does NOT exactly match `cursorDtg` — no
 *     position was recorded at this exact DTG, so the picked snapshot is a fallback to the
 *     nearest earlier one — render as "last known"/"not confirmed" (when `visible` is also
 *     `true`). A `stationary` track is always current regardless of exact match (it hasn't
 *     moved, so its last recorded position is still accurate).
 */
export function resolveTrackAtDtg(track, cursorDtg, maxAgeDays) {
    const snapshots = Array.isArray(track?.snapshots) ? track.snapshots : [];

    // No snapshot history at all — see the `snapshot: null` doc note above.
    if (snapshots.length === 0) {
        return {snapshot: null, isCurrent: true, visible: true};
    }

    const sorted = [...snapshots].sort((a, b) => a.dtg - b.dtg);
    const latestOverall = sorted[sorted.length - 1];

    // No live cursor wired in — behave as "always show the latest known state".
    if (cursorDtg == null) {
        return {snapshot: latestOverall, isCurrent: true, visible: true};
    }

    let picked = null;
    for (const s of sorted) {
        if (s.dtg <= cursorDtg) picked = s; // ascending order → last match is latest at-or-before
        else break;
    }

    // Track starts after the cursor — nothing to draw yet.
    if (!picked) {
        return {snapshot: null, isCurrent: false, visible: false};
    }

    const mobility = track?.mobility ?? null;
    // "Current" (no "last known" badge) iff the picked snapshot's dtg exactly matches the cursor
    // — i.e. there IS a recorded position at this exact DTG — or the track is stationary (its
    // last recorded position stays accurate regardless of exact match). Any other pick (cursor
    // between two snapshots, or scrubbed past the last one with no newer data) has no exact
    // position at this DTG, so it falls back to the nearest earlier snapshot and is stale.
    const isCurrent = mobility === 'stationary' || picked.dtg === cursorDtg;

    // ticket 04 (merge-last-known-into-trail): a stale pick older than the caller's `maxAgeDays`
    // ceiling is suppressed entirely — same `visible: false` signal as "hasn't started yet"
    // above — rather than returned for the caller to render with a "last known" badge.
    if (!isCurrent && maxAgeDays != null) {
        const ageMs = cursorDtg - picked.dtg;
        const maxAgeMs = Math.max(maxAgeDays, 0) * MS_PER_DAY;
        if (ageMs > maxAgeMs) {
            return {snapshot: null, isCurrent: false, visible: false};
        }
    }

    return {snapshot: picked, isCurrent, visible: true};
}

const MS_PER_DAY = 24 * 60 * 60 * 1000;

// ticket 05: movement trail opacity range — most recent past snapshot renders near-opaque
// (TRAIL_MAX_OPACITY), the oldest snapshot still inside the trailing window renders nearly
// invisible (TRAIL_MIN_OPACITY), with a linear fade between based on how many days old (as a
// fraction of the window) each snapshot is. Exported so tests/renderers can assert against the
// exact bounds rather than magic numbers.
export const TRAIL_MAX_OPACITY = 0.5;
export const TRAIL_MIN_OPACITY = 0.1;

/**
 * ticket 05: movement trail — sibling to `resolveTrackAtDtg` above. Instead of resolving a
 * single "what should render right now" snapshot, this gathers every OTHER snapshot within a
 * trailing `windowDays`-day window ending at `cursorDtg`, so the render pipeline can draw a
 * fading "ghost trail" of a moving track's recent past positions behind its current one.
 *
 * Pure and testable in isolation — no OL/DOM dependencies — same rationale as
 * `resolveTrackAtDtg`.
 *
 * @param {{snapshots?: Array<{dtg:number, geometry:object|null, props?:object}>|null}} track -
 *   A marking's attrs (or any object shaped like one) — only `snapshots` is read. Callers decide
 *   whether to invoke this at all based on `mobility` (trails are only meaningful for `moving`
 *   tracks; stationary tracks don't move, so there's nothing to trail) — this function doesn't
 *   gate on `mobility` itself, so it stays a pure "snapshots in window" utility.
 * @param {number|null|undefined} cursorDtg - epoch ms DTG cursor value. `null`/`undefined` (no
 *   live cursor wired in) returns `[]` — a trail is only meaningful relative to an actual cursor
 *   position, unlike `resolveTrackAtDtg`'s "no cursor -> show latest" fallback.
 * @param {number} [windowDays=7] - how many days back from `cursorDtg` to include.
 * @returns {Array<{dtg:number, geometry:object|null, props?:object, opacity:number}>} - past
 *   snapshots (excluding the "current" one `resolveTrackAtDtg` would resolve at `cursorDtg`,
 *   which the caller renders separately at full opacity) whose `dtg` falls in
 *   `[cursorDtg - windowDays*24h, cursorDtg]` inclusive, ordered oldest -> newest, each with an
 *   added `opacity` field (`TRAIL_MIN_OPACITY`..`TRAIL_MAX_OPACITY`, older = more faded).
 *
 *   ticket 04 (dtg-snapshot-rework): consecutive-in-time snapshots that share an exact position
 *   (e.g. a track edited more than once while parked) are collapsed into a single entry — see
 *   `groupTrailSnapshotsByPosition` below — before opacity is computed, so a genuinely-stationary
 *   run of edits yields one faded ghost per *position*, not one per snapshot, and the 50%->10%
 *   fade is spread evenly across the collapsed positions instead of the raw snapshot count.
 */
export function getTrailSnapshots(track, cursorDtg, windowDays = 7) {
    const snapshots = Array.isArray(track?.snapshots) ? track.snapshots : [];
    if (snapshots.length === 0 || cursorDtg == null) return [];

    const sorted = [...snapshots].sort((a, b) => a.dtg - b.dtg);

    // Same "latest snapshot at-or-before the cursor" pick as resolveTrackAtDtg — that snapshot
    // is the "current" position, rendered separately (full opacity, no fade) by the existing
    // ticket-04 pipeline; the trail only covers snapshots strictly older than it.
    let currentDtgValue = null;
    for (const s of sorted) {
        if (s.dtg <= cursorDtg) currentDtgValue = s.dtg;
        else break;
    }
    if (currentDtgValue == null) return []; // track isn't visible yet at this cursor

    const windowSpanMs = Math.max(windowDays, 0) * MS_PER_DAY;
    const windowStart = cursorDtg - windowSpanMs;

    const inWindow = sorted.filter((s) => s.dtg < currentDtgValue && s.dtg >= windowStart && s.dtg <= cursorDtg);
    const collapsed = groupTrailSnapshotsByPosition(inWindow);

    return collapsed.map((s) => {
        const ageMs = cursorDtg - s.dtg;
        const ageFraction = windowSpanMs > 0 ? Math.min(Math.max(ageMs / windowSpanMs, 0), 1) : 0;
        const opacity = TRAIL_MAX_OPACITY - (TRAIL_MAX_OPACITY - TRAIL_MIN_OPACITY) * ageFraction;
        return {...s, opacity};
    });
}

/**
 * ticket 04 (dtg-snapshot-rework): collapses runs of consecutive-in-time snapshots that sit at
 * the exact same position into a single representative entry — the newest snapshot of that run
 * — so a track edited multiple times without moving (e.g. amplifier changed twice while parked)
 * renders one trail ghost at that spot instead of a stack of duplicate symbols. Sibling to
 * `getTrailSnapshots`/`resolveTrackAtDtg`: pure, no OL/DOM dependencies, testable in isolation.
 *
 * Only *consecutive* same-position snapshots collapse — e.g. positions [A, B, A] stay 3 distinct
 * groups, since the two A's aren't adjacent in time (the track visited B in between, so it did
 * move away and back — not a single stationary run). A track that moves between every snapshot
 * is returned unchanged (one group per snapshot, same as the input).
 *
 * @param {Array<{dtg:number, geometry:object|null, props?:object}>} snapshots - ordered oldest
 *   -> newest (the same ordering `getTrailSnapshots` builds internally before attaching opacity).
 * @returns {Array<{dtg:number, geometry:object|null, props?:object}>} one entry per
 *   consecutive-same-position run, each the run's newest (last) snapshot — its `dtg`/`props` are
 *   "whatever was true most recently at that position". Order is preserved (oldest -> newest).
 */
export function groupTrailSnapshotsByPosition(snapshots) {
    const list = Array.isArray(snapshots) ? snapshots : [];
    const groups = [];
    for (const s of list) {
        const lastIdx = groups.length - 1;
        if (lastIdx >= 0 && samePosition(groups[lastIdx].geometry, s.geometry)) {
            // A newer snapshot at the same position — it replaces the run's representative since
            // it's the most recently-true data for this position.
            groups[lastIdx] = s;
        } else {
            groups.push(s);
        }
    }
    return groups;
}

// Exact-match position comparison for `groupTrailSnapshotsByPosition` — these are meant to catch
// literal duplicates-in-place (the same recorded position across edits), not "close together"
// positions, so a plain deep-equality check on type+coordinates is intentionally strict.
function samePosition(a, b) {
    if (a === b) return true;
    if (!a || !b || a.type !== b.type) return false;
    return JSON.stringify(a.coordinates) === JSON.stringify(b.coordinates);
}

/**
 * ticket 03 (layer-panel-rework): the Layers panel's per-layer DTG start/end display — scans
 * every marking on `layerId` and returns the min/max `dtg` across all of their `snapshots`
 * histories combined. Pure and testable in isolation, same rationale as `resolveTrackAtDtg`/
 * `getTrailSnapshots` above.
 *
 * @param {string} layerId
 * @param {Array<{layerId?: string, snapshots?: Array<{dtg:number}>|null}>|null|undefined} markings
 * @returns {{start: number|null, end: number|null}} `{null, null}` when the layer has no
 *   markings, or none of its markings have any snapshot history.
 */
export function computeLayerDtgRange(layerId, markings) {
    let start = null;
    let end = null;
    for (const m of Array.isArray(markings) ? markings : []) {
        if (m?.layerId !== layerId) continue;
        for (const s of Array.isArray(m.snapshots) ? m.snapshots : []) {
            if (typeof s?.dtg !== 'number') continue;
            if (start === null || s.dtg < start) start = s.dtg;
            if (end === null || s.dtg > end) end = s.dtg;
        }
    }
    return { start, end };
}

/**
 * map-node-dtg-default ticket: mapNode.js's `dtgSelectedMs` attr defaults to `null` ("no DTG
 * selected") at the PM schema level — a static schema `default` can't compute "the latest
 * available DTG" from live doc state, so that fallback is resolved here instead, at the point
 * each consumer reads the attr (see mapNode.js's `dtgSelectedMs` doc comment).
 *
 * "Latest available DTG" is defined as the most recent snapshot timestamp across ALL markings in
 * the shared map fragment (i.e. the most recent thing that actually happened) — NOT the shared
 * `map_meta.dtgStartMs`/`dtgEndMs` range, which is a manually-editable timeline window (see
 * mapSchema.js/useDtgRange.js) that can extend past, or fall short of, the actual marking data.
 * This mirrors the "no cursor -> show latest known state" convention already established by
 * `resolveTrackAtDtg`/`computeDefaultViewBbox`/`layersInView` above (`cursorDtg == null`), just
 * generalized across every marking instead of one track at a time — reuse that same "latest
 * known state" concept rather than a second, competing definition.
 *
 * Every consumer should call this ONE helper instead of re-implementing its own null-fallback
 * (e.g. `Date.now()`, or leaving a picker blank) — see MapNodeChrome.js/buildSitrepPayload.js/
 * dtgDiff.js's call sites.
 *
 * @param {{markings?: Array<{snapshots?: Array<{dtg:number}>|null}>|null}|null|undefined} mapFragment
 *   Any object exposing a `markings` array shaped like `extractMapState()`'s return (or a
 *   `MapFragmentStore`/`MapPreviewQueueService` `previewState`) — only `.markings` is read.
 * @param {number|null|undefined} dtgSelectedMs - the node's persisted `attrs.dtgSelectedMs`.
 * @returns {number|null} `dtgSelectedMs` unchanged when it's already a number (including `0`,
 *   the falsy-but-valid epoch guard used elsewhere for this attr), else the latest snapshot `dtg`
 *   across every marking's `snapshots`, or `null` if there is no snapshot history at all yet
 *   (e.g. a brand-new map with zero markings) — degrades gracefully rather than throwing.
 */
export function getEffectiveDtgSelected(mapFragment, dtgSelectedMs) {
    if (typeof dtgSelectedMs === 'number') return dtgSelectedMs;
    let latest = null;
    for (const m of Array.isArray(mapFragment?.markings) ? mapFragment.markings : []) {
        for (const s of Array.isArray(m?.snapshots) ? m.snapshots : []) {
            if (typeof s?.dtg !== 'number') continue;
            if (latest === null || s.dtg > latest) latest = s.dtg;
        }
    }
    return latest;
}

/** Convert map markings to MapLibre/OL GeoJSON features.
 *
 * ticket 01: every marking's renderable data (geometry, sidc, sidcModifiers, label, affiliation)
 * comes exclusively from its resolved `snapshots` entry — there's no top-level attr fallback.
 *
 * @param {Array} markings
 * @param {Array} layers
 * @param {number|null} [cursorDtg] - ticket 04: DTG cursor (epoch ms). `undefined`/`null` means
 *   "no live cursor" -> `resolveTrackAtDtg` resolves each marking's latest known snapshot
 *   (`isCurrent: true` for all). When supplied, tracks not yet "born" at the cursor are dropped
 *   and `properties.isCurrent` reflects whether the resolved snapshot is exactly at `cursorDtg`
 *   or a stale fallback to an earlier one.
 * @param {boolean} [trailEnabled=false] - ticket 04 (merge-last-known-into-trail): the existing
 *   "Show trail" toggle (MapEditorModal.jsx), reused here (no new setting) to bound how stale a
 *   "last known" pick may be before it's dropped from the output entirely rather than included
 *   with `isCurrent: false`. `false` (the default) means any non-exact-match pick is dropped —
 *   the "LAST KNOWN" badge only ever shows when the trail is on and the pick is within
 *   `trailWindowDays` of `cursorDtg` (see `resolveTrackAtDtg`'s `maxAgeDays` param).
 * @param {number} [trailWindowDays=7] - the existing trailing-window-in-days control
 *   (MapEditorModal.jsx), only consulted when `trailEnabled` is true.
 */
export function markingsToGeoJSON(markings, layers, cursorDtg, trailEnabled = false, trailWindowDays = 7) {
    const layerMap = new Map(layers.map((l) => [l.layerId, l]));
    const maxAgeDays = trailEnabled ? trailWindowDays : 0;
    const features = [];
    for (const m of markings) {
        if (layerMap.get(m.layerId)?.visible === false) continue;
        const resolved = resolveTrackAtDtg(m, cursorDtg, maxAgeDays);
        if (!resolved.visible || !resolved.snapshot) continue;
        const geometry = resolved.snapshot.geometry;
        if (!geometry) continue;
        // ticket 01: `snapshots` is the sole source of truth for time-varying render data —
        // sidc/sidcModifiers/label/affiliation live only inside the resolved snapshot's `props`,
        // never as a marking-level attr.
        const props = resolved.snapshot.props || {};
        const layer = layerMap.get(m.layerId) || {};
        features.push({
            type: 'Feature',
            id: m.markingId,
            geometry,
            properties: {
                mode: m.mode,
                elementType: m.elementType || null,
                label: props.label || layer.label || '',
                color: DEFAULT_MARKING_COLOR,
                visible: layer.visible !== false,
                layerId: m.layerId,
                author: m.author,
                createdAt: m.createdAt,
                updatedAt: m.updatedAt,
                // ticket 03: identity/mobility metadata — additive, existing consumers that
                // only read the fields above are unaffected.
                updatedBy: m.updatedBy || null,
                trackId: m.trackId || null,
                mobility: m.mobility || null,
                sidc: props.sidc || null,
                sidcModifiers: props.sidcModifiers || null,
                affiliation: props.affiliation || null,
                graphicType: m.graphicType || null,
                // ticket 04: whether the resolved snapshot above is stale ("last known") relative
                // to the DTG cursor — additive, same back-compat rationale as the ticket 03 fields.
                isCurrent: resolved.isCurrent,
            },
        });
    }
    return features;
}

/** Layer panel rows — one per map_layer node with live attrs. */
export function layersForPanel(layers) {
    return layers.map((l) => ({
        id: l.layerId,
        properties: {
            label: l.label,
            visible: l.visible,
            status: l.status || 'working',
            ownerId: l.ownerId ?? null,
        },
    }));
}

/** Build view options for selects (includes virtual default). */
export function buildViewOptions(views) {
    const options = [{id: DEFAULT_VIEW_ID, name: 'All markings'}];
    views.forEach((v) => options.push({id: v.viewId, name: v.name || 'View'}));
    return options;
}

/** Capture current map bounds as a bbox [[sw],[ne]]. */
export function bboxFromMap(map) {
    if (!map) return null;
    const b = map.getBounds();
    return [
        [b.getWest(), b.getSouth()],
        [b.getEast(), b.getNorth()],
    ];
}

/**
 * Capture the geographic bbox of a pixel-space rectangle inside an OL map canvas.
 * Uses map.getCoordinateFromPixel() on the four corners.
 *
 * @param {import('ol/Map').default} map
 * @param {{ left: number, top: number, width: number, height: number }} pixelRect
 * @returns {[[number,number],[number,number]]|null}
 */
export function bboxFromMapRect(map, pixelRect) {
    if (!map || !pixelRect) return null;
    const {left, top, width, height} = pixelRect;
    // Support both OL (getCoordinateFromPixel) and legacy MapLibre (unproject)
    if (map.getCoordinateFromPixel) {
        const {toLonLat} = require('ol/proj');
        const sw = toLonLat(map.getCoordinateFromPixel([left, top + height]));
        const ne = toLonLat(map.getCoordinateFromPixel([left + width, top]));
        if (!sw || !ne) return null;
        return [[sw[0], sw[1]], [ne[0], ne[1]]];
    }
    // MapLibre fallback (kept for any callers outside the editor)
    const sw = map.unproject([left, top + height]);
    const ne = map.unproject([left + width, top]);
    return [[sw.lng, sw.lat], [ne.lng, ne.lat]];
}

/**
 * Read the current PM doc out of a Y.XmlFragment synchronously.
 * Used by MapNodeView to render the inline preview and by mapStateFromFragment
 * to seed the editor's initial doc.
 * Returns null if the fragment is empty (map not yet edited by anyone).
 *
 * Uses yXmlFragmentToProseMirrorRootNode which converts the Y types directly
 * without requiring a live EditorView (unlike ySyncPlugin which only populates
 * the doc inside its view() hook).
 *
 * @param {import('yjs').XmlFragment} fragment
 * @returns {import('prosemirror-model').Node|null}
 */
export function mapDocFromFragment(fragment) {
    if (!fragment || fragment.length === 0) return null;
    try {
        return yXmlFragmentToProseMirrorRootNode(fragment, mapSchema);
    } catch (_) {
        return null;
    }
}
